import React, { useState, useEffect } from "react";
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Modal,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Grid, Stack, TablePagination
} from "@mui/material";
import http from "../../utils/http-common";
import { toast } from "react-toastify";
import { styled } from '@mui/material/styles';
import { Edit as EditIcon, ChangeCircle as ChangeCircleIcon } from '@mui/icons-material';
const StatusButton = styled(Box)(({ status }) => ({
  padding: '2px 8px',
  borderRadius: '20px',
  fontSize: '12px',
  fontWeight: 600,
  backgroundColor: 'transparent',
  border: `1px solid ${getStatusColor(status)}`,
  color: getStatusColor(status),
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '24px',
  minWidth: '90px',
  textTransform: 'capitalize'
}));

// Add this function before your component to get status color
const getStatusColor = (status) => {
  const statusColors = {
    'Open': '#2196F3',        // Blue
    'In Progress': '#FF9800', // Orange
    'Pending': '#FFA726',     // Light Orange
    'Requested': '#666666',   // Gray
    'Schedule': '#9C27B0',    // Purple
    'Technical': '#00ACC1',   // Cyan
    'Completed': '#4CAF50',   // Green
    'Blocked': '#F44336'      // Red
  };
  return statusColors[status] || '#666666'; // Default gray if status not found
};

export default function IssueRegister() {
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);

  // Add pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  const [issueData, setIssueData] = useState({
    id: "",
    issue_type: "",
    who_raised_it: "",
    description: "",
    priority: "",
    assignedTo: "",
    targetResolutionDate: "",
    final_solution: "",
    status: "Open"
  });

  const [issueModalOpen, setIssueModalOpen] = useState(false);
  const [issues, setIssues] = useState([]);
  const planId = localStorage.getItem("plan_id");
  useEffect(() => {
    fetchPlanData();
  }, [planId]);
  const fetchPlanData = async () => {
    try {
      const response = await http.get(`/master_service/get_project/plan/summary/${planId}`);
      if (response.status === 200) {
        const data = response.data.issues.map(item => ({
          id: item.issue_id,
          issue_type: item.issue_type,
          who_raised_it: item.who_raised_it,
          description: item.description,
          priority: item.priority,
          assignedTo: item.assigned_to,
          targetResolutionDate: item.target_resolution_date,
          final_solution: item.final_solution,
          status: item.status
        }));
        setIssues(data);
      }
    } catch (error) {
      console.error("Error fetching user data", error);
    }
  };
  const [resources, setResources] = useState([]);
  useEffect(() => {
    const fetchDetails = async () => {
      try {
        const response = await http.post("master_service/plan_role/user/mapping/details", { plan_id: planId });
         if (response.data?.mappings?.length > 0) {
        const fetchedResources = response.data.mappings
          .filter(item => item.user?.email) // ✅ Only include records with email
          .map(item => ({
            label: item.user?.user_name || "",
            value: item.user?.email, // ✅ Use email as value
          }));
          setResources(fetchedResources);
          if (!issueData.assignedTo && fetchedResources.length > 0) {
            setIssueData(prev => ({ ...prev, assignedTo: fetchedResources[0].value }));
          }
        }
      } catch (error) {
        console.error("Error fetching resources:", error);
        if (error.response?.status === 404) {
          console.error("Error 404 : No resources found for this plan.");
          setResources([]);
        }
      }
    };

    fetchDetails();
  }, [planId]);
  const [filters, setFilters] = useState({
    issues: '',
    assumptions: '',
    risks: '',
    dependencies: '',
    constraints: ''
  });
  // Add filter functions for each table
  const filterIssues = () => {
    return issues.filter(issue =>
      Object.values(issue).some(value =>
        value ? value.toString().toLowerCase().includes(filters.issues.toLowerCase()) : false
      )
    );
  };
  const [expandedSections, setExpandedSections] = useState({});
  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };
  const handleInputChange = (field, value) => {
    setIssueData(prev => ({ ...prev, [field]: value }));
  };
  const handleFilterChange = (table, value) => {
    setFilters(prev => ({
      ...prev,
      [table]: value
    }));
  };

  const handleEdit = (issue) => {
    setIssueData({
      id: issue.id,
      issue_type: issue.issue_type,
      description: issue.description,
      priority: issue.priority,
      assignedTo: issue.assignedTo,
      status: issue.status,
      who_raised_it: issue.who_raised_it,
      targetResolutionDate: issue.targetResolutionDate
    });
    setIssueModalOpen(true);
  };
  const handleStatusUpdate = async (issue) => {

    const nextStatus = getNextStatus(issue.status);
    try {
      const payload = {
        issue_id: issue.id,
        "description": issue.description,
        "priority": issue.priority,
        "issue_type": issue.issue_type,
        "who_raised_it": issue.who_raised_it,
        "plan_id": planId,
        "assigned_to": issue.assignedTo,
        "target_resolution_date": issue.targetResolutionDate,
        status: nextStatus,
      };
      const response = await http.post('/master_service/create_update/issues_data', payload);
      if (response.status === 200 || response.status === 201) {
        toast.success("Status updated successfully");
        fetchPlanData();
      }
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  // Helper function to determine next status
  const getNextStatus = (currentStatus) => {
    const statusFlow = {
      'Open': 'In Progress',
      'In Progress': 'Completed',
      'Completed': 'Open',
      'Blocked': 'Open',
      'Technical': 'In Progress',
      'Pending': 'In Progress',
      'Requested': 'In Progress',
      'Schedule': 'In Progress'
    };
    return statusFlow[currentStatus] || 'Open';
  };
  const handleSave = async (data, closeModal, resetData) => {
    try {
      const payload = {
        issue_id: data.id,
        "description": data.description,
        "priority": data.priority,
        "issue_type": data.issue_type,
        "who_raised_it": data.who_raised_it,
        "plan_id": planId,
        "assigned_to": data.assignedTo,
        "target_resolution_date": data.targetResolutionDate,
        final_solution: data.final_solution,
        "status": data.status
      }
      const response = await http.post('/master_service/create_update/issues_data', payload);
      if (response.status === 201 || response.status === 200) {
        toast.success("Data saved successfully");
        closeModal(false);
        resetData();
        fetchPlanData();
      }
    } catch (error) {
      toast.error("Failed to save data1");
    }
  };

  return (
    <Container maxWidth={false} sx={{ width: "100%", p: 1 }}>
      {/* Issue Log Table */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: '#F7F8F8', p: 0.5, mb: 0.2 }}>
        <Box sx={{ display: "flex", justifyContent: "flex-start", alignItems: "center" }}>
          <Typography style={{ fontWeight: "600" }}>Issue Register</Typography>
          <Typography sx={{ ml: 1, fontSize: '12px' }}>({issues.length} Issues)</Typography>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
          <TextField
            size="small"
            placeholder="Search here..."
            value={filters.issues}
            onChange={(e) => handleFilterChange('issues', e.target.value)}
            sx={{ ml: 2, mr: 1, minWidth: '200px' }}
          />
          <Button
            onClick={() => {
              setIssueModalOpen(true)
              setIssueData({});
            }}
            sx={{
              minWidth: '24px',
              width: '26px',
              height: '26px',
              p: 0,
              mr: '4px',
              color: '#FFF',
              border: 'none',
              backgroundColor: '#FF2800',
              '&:hover': {
                backgroundColor: '#FF2800',
                border: 'none'
              }
            }}
          >
            +
          </Button>
        </Box>
      </Box>
      <TableContainer component={Paper} sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead sx={{ backgroundColor: "#e0eaff" }}>
            <TableRow sx={{ '& .MuiTableCell-head': { py: 0.1, fontSize: '12px', fontWeight: 500 } }}>
              <TableCell>ID</TableCell>
              <TableCell>Issue Type</TableCell>
              <TableCell>Who Raised It</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell>Assigned To</TableCell>
              <TableCell>Target Resolution Date</TableCell>
              <TableCell>Final Solution</TableCell>
              <TableCell>Status</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filterIssues()
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((issue, index) => (
                <TableRow key={index} sx={{ '& .MuiTableCell-body': { py: 0.2 } }}>
                  <TableCell>{issue.id}</TableCell>
                  <TableCell>{issue.issue_type}</TableCell>
                  <TableCell>{issue.who_raised_it}</TableCell>
                  <TableCell>{issue.description}</TableCell>
                  <TableCell>{issue.priority}</TableCell>
                  <TableCell>{issue.assignedTo}</TableCell>
                  <TableCell>{issue.targetResolutionDate}</TableCell>
                  <TableCell>{issue.final_solution}</TableCell>
                  <TableCell><StatusButton status={issue.status}>{issue.status}</StatusButton></TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(issue)}
                        sx={{ p: 0.5 }}
                      >
                        <EditIcon sx={{ height: "18px", width: "18px", color: "#666" }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleStatusUpdate(issue)}
                        sx={{ p: 0.5 }}
                      >
                        <ChangeCircleIcon sx={{ height: "18px", width: "18px", color: getStatusColor(issue.status) }} />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
        {filterIssues().length > 10 && (
          <TablePagination
            rowsPerPageOptions={[10]}
            component="div"
            count={filterIssues().length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            sx={{
              '.MuiTablePagination-toolbar': {
                minHeight: '40px',
              },
              '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                fontSize: '12px',
              }
            }}
          />
        )}
      </TableContainer>
      <Modal
        open={issueModalOpen}
        onClose={() => setIssueModalOpen(false)}
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "flex-end"
        }}
      >
        <Box
          sx={{
            width: "350px",
            height: "100%",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 1,
            overflowY: "auto",
            '& .MuiInputBase-input': { // Add this style to reduce input font size
              fontSize: '0.875rem'
            },
            '& .MuiInputLabel-root': { // Add this style to reduce label font size
              fontSize: '0.875rem'
            },
            '& .MuiMenuItem-root': { // Add this style to reduce dropdown item font size
              fontSize: '0.875rem'
            }
          }}
        >
          <Typography mb={1.5} style={{ font: 'bold', fontWeight: '600' }}>Add Issue</Typography>

          <FormControl fullWidth sx={{ mb: 1.5 }} size="small">
            <InputLabel>Issue Type</InputLabel>
            <Select
              value={issueData.issue_type || "Others"}
              label="Issue Type"
              onChange={(e) => handleInputChange("issue_type", e.target.value)}
              size="small"
              renderValue={(selected) => selected || "Others"}
            >
              <MenuItem value="Technical">Technical</MenuItem>
              <MenuItem value="Requirement">Requirement</MenuItem>
              <MenuItem value="Resource">Resource</MenuItem>
              <MenuItem value="Others">Others</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ mb: 1.5 }} size="small">
            <InputLabel id="raised-from" shrink={true}>Raised From</InputLabel>
            <Select
              value={issueData.who_raised_it || ""}
              labelId="raised-from"
              label="Raised From"
              onChange={(e) => handleInputChange("who_raised_it", e.target.value)}
              size="small"
              notched
            >
              <MenuItem value="Dev Team">Dev Team</MenuItem>
              <MenuItem value="Product Owner">Product Owner</MenuItem>
              <MenuItem value="PM">PM</MenuItem>
              <MenuItem value="QA Engineer">QA Engineer</MenuItem>
              <MenuItem value="Stakeholder">Stakeholder</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            multiline
            rows={3}
            label="Description"
            value={issueData.description}
            onChange={(e) => handleInputChange("description", e.target.value)}
            sx={{ mb: 1.5 }}
            size="small"
            inputProps={{ maxLength: 200 }}
          />

          <FormControl fullWidth sx={{ mb: 1.5 }} size="small">
            <InputLabel id="priority" shrink={true}>Priority</InputLabel>
            <Select
              value={issueData.priority}
              labelId="priority"
              label="Priority"
              onChange={(e) => handleInputChange("priority", e.target.value)}
              size="small"
              notched
            >
              <MenuItem value="High">High</MenuItem>
              <MenuItem value="Medium">Medium</MenuItem>
              <MenuItem value="Low">Low</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ mb: 1.5 }} size="small">
            <InputLabel>Assigned To</InputLabel>
            <Select
              value={issueData.assignedTo || ""}
              label="Assigned To"
              onChange={(e) => handleInputChange("assignedTo", e.target.value)}
              size="small"
              renderValue={(selected) => {
      if (!selected) return "Select User"; // ✅ Placeholder
      const selectedUser = resources.find((r) => r.value === selected);
      return selectedUser ? selectedUser.label : selected; // ✅ Show name instead of email
    }}
            >
              {resources.map((resource) => (
                <MenuItem key={resource.label} value={resource.value}>
                  {resource.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            type="date"
            label="Target Resolution Date"
            value={issueData.targetResolutionDate}
            onChange={(e) => handleInputChange("targetResolutionDate", e.target.value)}
            sx={{ mb: 1.5 }}
            InputLabelProps={{
              shrink: true, // keeps label visible above the date field
            }}
            inputProps={{
              min: new Date().toISOString().split("T")[0]  // ✅ Only allow today & future
            }}
            size="small"
          />
         <TextField
            fullWidth
            multiline
            rows={2}
            label="Final Solution"
            value={issueData.final_solution}
            onChange={(e) => handleInputChange("final_solution", e.target.value)}
            sx={{ mb: 1.5 }}
            size="small"
            inputProps={{ maxLength: 100 }}
          />
          <Button
            fullWidth
            onClick={() => handleSave(issueData, setIssueModalOpen, () => setIssueData({
              id: "",
              issue_type: "Others",
              description: "",
              priority: "",
              assignedTo: "",
              targetResolutionDate: "",
              who_raised_it: ""
            }))}
            sx={{
              textTransform: "none", bgcolor: '#FF2800', color: 'white',
              "&:hover": {
                bgcolor: "#FF2800", // keep same color on hover
              },
            }}
          >
            Save
          </Button>

          <Button
            fullWidth
            onClick={() => {
              setIssueModalOpen(false)
              setIssueData({});
            }}
            sx={{ textTransform: "none", bgcolor: 'white', color: 'black' }}
          >
            Cancel
          </Button>
        </Box>
      </Modal>
    </Container>
  )
}